import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router';
import { adminGenerationsApi, type AdminGenerationFilters } from '../api/admin-generations.js';
import { useAuth } from '../auth/context.js';
import { useI18n } from '../i18n/context.js';
import { Select } from '../components/Select.js';
import { AdminQueuePanel } from './AdminQueuePanel.js';

export function AdminGenerationsPage(): JSX.Element {
  const { user } = useAuth();
  const { t, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<AdminGenerationFilters>({ sort: 'time' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [negative, setNegative] = useState('');
  const [modelId, setModelId] = useState('');
  const [seed, setSeed] = useState('');
  const [sizeRequested, setSizeRequested] = useState('{}');
  const [requestPayload, setRequestPayload] = useState('{}');
  const [revealed, setRevealed] = useState<readonly { assetId: string; url: string }[]>([]);
  const [sceneId, setSceneId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['admin-generations', filters],
    queryFn: () => adminGenerationsApi.list(filters),
    enabled: user?.role === 'admin',
  });
  const detail = useQuery({
    queryKey: ['admin-generation', selectedId],
    queryFn: () => adminGenerationsApi.detail(selectedId!),
    enabled: user?.role === 'admin' && Boolean(selectedId),
  });

  useEffect(() => {
    if (!detail.data) return;
    setPrompt(detail.data.promptFinal ?? '');
    setNegative(detail.data.negativePrompt ?? '');
    setModelId(detail.data.modelId);
    setSeed(detail.data.seed ?? '');
    setSizeRequested(prettyJson(detail.data.sizeRequested));
    setRequestPayload(prettyJson(detail.data.requestPayload));
    setRevealed([]);
  }, [detail.data]);

  if (user?.role !== 'admin')
    return (
      <section className="mx-auto max-w-3xl">
        <p role="alert">{t.admin.forbidden}</p>
        <Link to="/app" className="kitup-quiet-button mt-4">
          {t.builder.back}
        </Link>
      </section>
    );

  const replay = (): void => {
    if (!selectedId) return;
    setMessage(null);
    let parsedSize: Record<string, unknown>;
    let parsedRequest: Record<string, unknown>;
    try {
      parsedSize = parseJsonRecord(sizeRequested);
      parsedRequest = parseJsonRecord(requestPayload);
    } catch {
      setMessage(t.admin.invalidJson);
      return;
    }
    void adminGenerationsApi
      .replay(selectedId, {
        promptFinal: prompt,
        negativePrompt: negative || null,
        modelId,
        seed: seed || undefined,
        sizeRequested: parsedSize,
        requestPayload: parsedRequest,
      })
      .then(() => {
        setMessage(t.admin.replayQueued);
        void queryClient.invalidateQueries({ queryKey: ['admin-generations'] });
        void queryClient.invalidateQueries({ queryKey: ['admin-generation', selectedId] });
      })
      /*
       * Здесь, в отличие от пользовательских экранов, показывается именно
       * текст сервера: админку читает тот, кто разбирает сбой генерации,
       * и диагностическое сообщение ему полезнее общей фразы.
       */
      .catch((error: unknown) =>
        setMessage(error instanceof Error ? error.message : t.common.unexpectedError),
      );
  };

  return (
    <main className="mx-auto max-w-[1500px] p-4 sm:p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t.admin.title}</h1>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.admin.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/book-builder" className="kitup-quiet-button">
            {t.admin.bookBuilder.title}
          </Link>
          <Link to="/admin/accounts" className="kitup-quiet-button">
            {t.admin.accounts.title}
          </Link>
          <Link to="/admin/usage" className="kitup-quiet-button">
            {t.admin.usage.title}
          </Link>
          <Link to="/admin/promotions" className="kitup-quiet-button">
            {t.admin.promotions.title}
          </Link>
          <Link to="/app" className="kitup-quiet-button">
            {t.admin.toApp}
          </Link>
        </div>
      </div>
      <AdminQueuePanel />

      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <Filter
          value={filters.provider ?? ''}
          label={t.admin.filters.provider}
          allLabel={t.admin.all}
          options={['wavespeed', 'anthropic', 'openai', 'local']}
          onChange={(provider) => setFilters({ ...filters, provider: provider || undefined })}
        />
        <InputFilter
          value={filters.modelId ?? ''}
          label={t.admin.filters.model}
          onChange={(value) => setFilters({ ...filters, modelId: value || undefined })}
        />
        <Filter
          value={filters.status ?? ''}
          label={t.admin.filters.status}
          allLabel={t.admin.all}
          options={['queued', 'running', 'succeeded', 'failed', 'cancelled']}
          optionLabel={(status) =>
            t.admin.statuses[status as keyof typeof t.admin.statuses] ?? status
          }
          onChange={(status) => setFilters({ ...filters, status: status || undefined })}
        />
        <Filter
          value={filters.targetType ?? ''}
          label={t.admin.filters.target}
          allLabel={t.admin.all}
          options={[
            'character_sheet',
            'thing_ref',
            'location_ref',
            'scene_image',
            'cover',
            'back_cover',
            'scene_video',
            'narration',
            'story',
            'scene_prompt',
            'collage',
          ]}
          optionLabel={(target) =>
            t.admin.targets[target as keyof typeof t.admin.targets] ?? target
          }
          onChange={(targetType) => setFilters({ ...filters, targetType: targetType || undefined })}
        />
        <Filter
          value={filters.sort ?? 'time'}
          label={t.admin.filters.sort}
          allLabel={t.admin.all}
          options={['time', 'cost', 'latency']}
          optionLabel={(sort) => t.admin.sorts[sort as keyof typeof t.admin.sorts] ?? sort}
          onChange={(sort) => setFilters({ ...filters, sort: sort as 'time' | 'cost' | 'latency' })}
        />
        <InputFilter
          value={filters.userId ?? ''}
          label={t.admin.filters.user}
          onChange={(value) => setFilters({ ...filters, userId: value || undefined })}
        />
        <InputFilter
          value={filters.bookId ?? ''}
          label={t.admin.filters.book}
          onChange={(value) => setFilters({ ...filters, bookId: value || undefined })}
        />
        <InputFilter
          value={filters.from?.slice(0, 10) ?? ''}
          label={t.admin.filters.from}
          type="date"
          onChange={(value) =>
            setFilters({ ...filters, from: value ? `${value}T00:00:00.000Z` : undefined })
          }
        />
        <InputFilter
          value={filters.to?.slice(0, 10) ?? ''}
          label={t.admin.filters.to}
          type="date"
          onChange={(value) =>
            setFilters({ ...filters, to: value ? `${value}T23:59:59.999Z` : undefined })
          }
        />
      </div>
      <div className="grid gap-5 lg:grid-cols-[minmax(360px,0.9fr)_minmax(0,1.5fr)]">
        <div className="kitup-card overflow-auto">
          {list.isPending ? <p className="p-4">{t.common.loading}</p> : null}
          <ul className="divide-y">
            {(list.data?.items ?? []).map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(item.id)}
                  aria-pressed={selectedId === item.id}
                  className="grid w-full gap-1 p-4 text-left aria-pressed:bg-[var(--kitup-color-surface-soft)]"
                >
                  <span className="flex justify-between gap-2">
                    <b>{item.modelId}</b>
                    <span>{t.admin.statuses[item.status]}</span>
                  </span>
                  <span className="truncate text-sm">{item.promptPreview || '—'}</span>
                  <span className="text-xs text-[var(--kitup-color-muted)]">
                    {item.targetType} · {formatDate(item.queuedAt)} · {t.admin.latency}:{' '}
                    {item.latencyMs === null ? '—' : `${item.latencyMs} ms`} · {t.admin.cost}:{' '}
                    {item.costUnits ?? '—'}
                  </span>
                  <span className="truncate text-xs text-[var(--kitup-color-muted)]">
                    {item.user.email} · {item.book?.title ?? t.admin.noBook}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="kitup-card min-w-0 p-5">
          {!selectedId ? (
            <p>{t.admin.select}</p>
          ) : detail.isPending ? (
            <p>{t.common.loading}</p>
          ) : detail.data ? (
            <div className="grid gap-5">
              <div>
                <h2 className="text-xl font-semibold">{detail.data.modelId}</h2>
                <p className="text-sm text-[var(--kitup-color-muted)]">
                  {detail.data.provider} · {detail.data.targetType} ·{' '}
                  {t.admin.statuses[detail.data.status]}
                </p>
                <p className="text-sm text-[var(--kitup-color-muted)]">
                  {t.admin.latency}: {detail.data.latencyMs ?? '—'} ms · {t.admin.cost}:{' '}
                  {detail.data.costUnits ?? '—'} · {detail.data.user.email} ·{' '}
                  {detail.data.book?.title ?? t.admin.noBook}
                </p>
              </div>
              <section>
                <h3 className="mb-2 font-semibold">{t.admin.promptParts}</h3>
                <div className="grid gap-2">
                  {promptParts(detail.data.promptParts).map((part, index) => (
                    <div
                      key={`${part.role}-${index}`}
                      className={`rounded-lg p-3 text-sm kitup-prompt-${part.role}`}
                    >
                      <b className="mr-2">{roleLabel(t.admin.roles, part.role)}</b>
                      {part.text}
                    </div>
                  ))}
                </div>
              </section>
              <label className="grid gap-1 text-sm">
                <span>{t.admin.prompt}</span>
                <textarea
                  rows={8}
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm">
                  <span>{t.admin.filters.model}</span>
                  <input value={modelId} onChange={(event) => setModelId(event.target.value)} />
                </label>
                <label className="grid gap-1 text-sm">
                  <span>{t.admin.seed}</span>
                  <input value={seed} onChange={(event) => setSeed(event.target.value)} />
                </label>
              </div>
              <label className="grid gap-1 text-sm">
                <span>{t.admin.size}</span>
                <textarea
                  rows={4}
                  value={sizeRequested}
                  onChange={(event) => setSizeRequested(event.target.value)}
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span>{t.admin.parameters}</span>
                <textarea
                  rows={6}
                  value={requestPayload}
                  onChange={(event) => setRequestPayload(event.target.value)}
                />
              </label>
              <section>
                <h3 className="mb-2 font-semibold">{t.admin.references}</h3>
                <p className="break-all text-xs text-[var(--kitup-color-muted)]">
                  {detail.data.refAssetIds.join(', ') || t.admin.noReferences}
                </p>
              </section>
              <label className="grid gap-1 text-sm">
                <span>{t.admin.negative}</span>
                <textarea
                  rows={3}
                  value={negative}
                  onChange={(event) => setNegative(event.target.value)}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="kitup-primary-button" onClick={replay}>
                  {t.admin.replay}
                </button>
                <button
                  type="button"
                  className="kitup-secondary-button"
                  onClick={() =>
                    void adminGenerationsApi
                      .copyCurl(detail.data!.id)
                      .then(({ curl }) => navigator.clipboard.writeText(curl))
                      .then(() => setMessage(t.admin.copied))
                  }
                >
                  {t.admin.copyCurl}
                </button>
                {detail.data.refAssetIds.length > 0 ? (
                  <button
                    type="button"
                    className="kitup-secondary-button"
                    onClick={() =>
                      void adminGenerationsApi
                        .revealReferences(detail.data!.id)
                        .then((result) => setRevealed(result.items))
                    }
                  >
                    {t.admin.reveal}
                  </button>
                ) : null}
              </div>
              {message ? (
                <p role="status" className="text-sm">
                  {message}
                </p>
              ) : null}
              {revealed.length > 0 ? (
                <div className="flex gap-2 overflow-x-auto">
                  {revealed.map((asset) => (
                    <img
                      key={asset.assetId}
                      src={asset.url}
                      alt={t.admin.referenceAlt}
                      className="h-32 w-32 rounded-lg object-cover"
                    />
                  ))}
                </div>
              ) : null}
              <section>
                <h3 className="mb-2 font-semibold">{t.admin.comparison}</h3>
                <div className="flex gap-3 overflow-x-auto">
                  {detail.data.comparison.map((run) => (
                    <button
                      key={run.id}
                      type="button"
                      className="kitup-card grid w-44 shrink-0 gap-2 p-2 text-left"
                      onClick={() => setSelectedId(run.id)}
                    >
                      {run.url ? (
                        <img
                          src={run.url}
                          alt={t.admin.comparison}
                          className="aspect-square w-full rounded-lg object-cover"
                        />
                      ) : (
                        <span className="grid aspect-square place-items-center rounded-lg bg-slate-100 text-xs">
                          {t.admin.noResult}
                        </span>
                      )}
                      <span className="truncate text-xs">{run.promptPreview}</span>
                      <span className="text-xs">{t.admin.statuses[run.status]}</span>
                    </button>
                  ))}
                </div>
              </section>
              <details>
                <summary className="cursor-pointer font-semibold">{t.admin.raw}</summary>
                <pre className="mt-2 max-h-80 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-white">
                  {JSON.stringify(
                    {
                      request: detail.data.requestPayload,
                      response: detail.data.responsePayload,
                      attempts: detail.data.attempts,
                      metadata: {
                        seed: detail.data.seed,
                        sizeRequested: detail.data.sizeRequested,
                        httpStatus: detail.data.httpStatus,
                        providerJobId: detail.data.providerJobId,
                        tokensIn: detail.data.tokensIn,
                        tokensOut: detail.data.tokensOut,
                        error: detail.data.error,
                        errorCode: detail.data.errorCode,
                      },
                    },
                    null,
                    2,
                  )}
                </pre>
              </details>
              {detail.data.origin === 'admin_replay' && detail.data.status === 'succeeded' ? (
                <div className="grid gap-2">
                  <label className="grid gap-1 text-sm">
                    <span>{t.admin.sceneId}</span>
                    <input value={sceneId} onChange={(event) => setSceneId(event.target.value)} />
                  </label>
                  <button
                    type="button"
                    className="kitup-secondary-button justify-self-start"
                    disabled={!sceneId}
                    onClick={() =>
                      void adminGenerationsApi
                        .apply(detail.data!.id, sceneId)
                        .then(() => setMessage(t.admin.applied))
                    }
                  >
                    {t.admin.apply}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <p role="alert">{t.common.unexpectedError}</p>
          )}
        </div>
      </div>
    </main>
  );
}

function Filter({
  value,
  label,
  allLabel,
  options,
  optionLabel,
  onChange,
}: {
  value: string;
  label: string;
  allLabel: string;
  options: readonly string[];
  optionLabel?(option: string): string;
  onChange(value: string): void;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span>{label}</span>
      <Select
        value={value}
        options={[
          { value: '', label: allLabel },
          ...options.map((option) => ({ value: option, label: optionLabel?.(option) ?? option })),
        ]}
        onChange={onChange}
      />
    </label>
  );
}

function InputFilter({
  value,
  label,
  type = 'text',
  onChange,
}: {
  value: string;
  label: string;
  type?: 'text' | 'date';
  onChange(value: string): void;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span>{label}</span>
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function prettyJson(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function parseJsonRecord(value: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(value);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error();
  return parsed as Record<string, unknown>;
}

function promptParts(value: unknown): readonly { role: string; text: string }[] {
  if (typeof value !== 'object' || value === null) return [];
  const parts = (value as { parts?: unknown }).parts;
  if (!Array.isArray(parts)) return [];
  return parts.filter(
    (part): part is { role: string; text: string } =>
      typeof part === 'object' &&
      part !== null &&
      typeof (part as { role?: unknown }).role === 'string' &&
      typeof (part as { text?: unknown }).text === 'string',
  );
}

function roleLabel(
  labels: Readonly<Record<'style' | 'subject' | 'layout' | 'suffix', string>>,
  role: string,
): string {
  return role in labels ? labels[role as keyof typeof labels] : role;
}
